import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import ProtectedRoute from './Components/Auth/ProtectedRoute';
import AdminRoute from './Components/Auth/AdminRoute';
import LoginModal from './Components/Auth/LoginModal';
import Layout from './Layout';
import NotFound from './Components/Shared/NotFound';
import PageSpinner from './Components/Shared/PageSpinner';
import OurPlacesV2Boundary from './Components/OurPlacesV2/Shared/ErrorBoundary';
import OurPlacesEntry, {
  RedirectTo, OUR_PLACES_PATH, OUR_PLACES_SEARCH_PATH,
} from './Components/OurPlacesV2/Shared/OurPlacesEntry';

/* Shared button + dropdown styling — imported last so they settle the cascade
   over Bootstrap and the per-page stylesheets. */
import './styles/buttons.css';
import './styles/dropdowns.css';

/* Every page below is loaded on demand.
 *
 * Importing them eagerly meant a visitor landing on the home page downloaded
 * Leaflet, the marker-cluster plugin, both map pages and all ten admin screens
 * before anything could paint — one 350 kB script for a page that needs a
 * fraction of it. Layout renders a spinner inside <main> while a chunk arrives,
 * so the header and footer stay put during the swap.
 *
 * Route-level splitting only: components shared across pages (Nav, Footer, the
 * auth modal) stay in the main chunk where they belong. */
const Home              = lazy(() => import('./Components/Home/home'));
const Experts           = lazy(() => import('./Components/Experts/experts'));
const ExpertDetails     = lazy(() => import('./Components/Experts/expertDetails'));
const KnowledgeCenter   = lazy(() => import('./Components/KnowledgeCenter/knowledgeCenter'));
const Resources         = lazy(() => import('./Components/KnowledgeCenter/resources'));
const Register          = lazy(() => import('./Components/Register/register'));
const SignupPage        = lazy(() => import('./Components/Auth/SignupPage'));
const Videos            = lazy(() => import('./Components/Videos/videos'));
const Donate            = lazy(() => import('./Components/Donate/donate'));
const OurplacesAI       = lazy(() => import('./Components/OurplacesAI/OurplacesAI'));
const Profile           = lazy(() => import('./Components/Profile/Profile'));
const BlobStorageTest   = lazy(() => import('./Components/BlobStorageTest'));
const PrivacyPolicy     = lazy(() => import('./Components/Legal/Legal').then((m) => ({ default: m.PrivacyPolicy })));
const TermsOfUse        = lazy(() => import('./Components/Legal/Legal').then((m) => ({ default: m.TermsOfUse })));
const PartnerPage       = lazy(() => import('./Components/Partners/PartnerPage'));

// Our Places — the chat-driven workspace (formerly /ourplacesv2) and its
// classic directory. The v1 map only renders for legacy campaign links; see
// OurPlacesEntry.
const OurPlacesV2Results   = lazy(() => import('./Components/OurPlacesV2/Results/Results'));

// Admin — ten CRUD screens no public visitor ever loads.
const AdminLayout        = lazy(() => import('./Components/Admin/AdminLayout'));
const AdminDashboard     = lazy(() => import('./Components/Admin/AdminDashboard'));
const AdminLeads         = lazy(() => import('./Components/Admin/AdminLeads'));
const AdminAsks          = lazy(() => import('./Components/Admin/AdminAsks'));
const AdminRegistrations = lazy(() => import('./Components/Admin/AdminRegistrations'));
const AdminIntegrations  = lazy(() => import('./Components/Admin/AdminIntegrations'));
const AdminReports       = lazy(() => import('./Components/Admin/AdminReports'));
const AdminExplore       = lazy(() => import('./Components/Admin/AdminExplore'));
const AdminSponsors      = lazy(() => import('./Components/Admin/AdminSponsors'));
const AdminExperts       = lazy(() => import('./Components/Admin/AdminExperts'));
const AdminVideos        = lazy(() => import('./Components/Admin/AdminVideos'));
const AdminResources     = lazy(() => import('./Components/Admin/AdminResources'));
const AdminVideoSections = lazy(() => import('./Components/Admin/AdminVideoSections'));
const AdminSiteContent   = lazy(() => import('./Components/Admin/AdminSiteContent'));
const AdminPartners      = lazy(() => import('./Components/Admin/AdminPartners'));
const AdminTopics        = lazy(() => import('./Components/Admin/AdminTopics'));
const AdminStages        = lazy(() => import('./Components/Admin/AdminStages'));
const AdminSpecialties   = lazy(() => import('./Components/Admin/AdminSpecialties'));

function RootShell() {
  const { loginModal, closeLogin } = useAuth();
  return (
    <>
      <Outlet />
      <LoginModal
        show={loginModal.open}
        onHide={closeLogin}
        initialMode={loginModal.mode}
      />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootShell />,
    children: [
  {
    path: '/admin',
    element: (
      <AdminRoute>
        <Suspense fallback={<PageSpinner minHeight="100vh" />}>
          <AdminLayout />
        </Suspense>
      </AdminRoute>
    ),
    errorElement: <NotFound asErrorElement />,
    children: [
      { index: true, element: <AdminDashboard /> },
      { path: 'leads', element: <AdminLeads /> },
      { path: 'asks', element: <AdminAsks /> },
      { path: 'registrations', element: <AdminRegistrations /> },
      { path: 'integrations', element: <AdminIntegrations /> },
      { path: 'reports', element: <AdminReports /> },
      { path: 'explore', element: <AdminExplore /> },
      { path: 'sponsors', element: <AdminSponsors /> },
      { path: 'experts', element: <AdminExperts /> },
      { path: 'videos', element: <AdminVideos /> },
      { path: 'resources', element: <AdminResources /> },
      { path: 'sections', element: <AdminVideoSections /> },
      { path: 'content', element: <AdminSiteContent /> },
      { path: 'partners', element: <AdminPartners /> },
      { path: 'topics', element: <AdminTopics /> },
      { path: 'stages', element: <AdminStages /> },
      { path: 'specialties', element: <AdminSpecialties /> },
    ]
  },
  {
    path: '/',
    element: <Layout />,
    /* Rendered inside Layout, so a thrown route error still leaves the visitor
       with a header, a footer and a way onward. */
    errorElement: <Layout><NotFound asErrorElement /></Layout>,
    children: [
      { index: true, element: <Home />, handle: { title: 'Join Our Place' } },
      // Boundaries are per-route rather than around both: a crash on the
      // workspace must not take the results page down with it. The workspace
      // (chat + canvas) replaced the v1 map at launch; the classic directory
      // experience lives at /ourplaces/search.
      { path: 'ourplaces', element: <OurPlacesEntry />, handle: { title: 'Our Places' } },
      {
        path: 'ourplaces/search',
        element: <OurPlacesV2Boundary><OurPlacesV2Results /></OurPlacesV2Boundary>,
        handle: { title: 'Our Places' },
      },
      // Beta-era and v1 URLs keep working — query strings (saved searches,
      // ?workspace= from the profile, campaign links) ride along.
      { path: 'ourplacesv2', element: <RedirectTo path={OUR_PLACES_PATH} /> },
      { path: 'ourplacesv2/search', element: <RedirectTo path={OUR_PLACES_SEARCH_PATH} /> },
      { path: 'map', element: <RedirectTo path={OUR_PLACES_PATH} /> },
      { path: 'ourplacesai', element: <OurplacesAI />, handle: { title: 'Ask Our Place' } },
      { path: 'donate', element: <Donate />, handle: { title: 'Donate' } },
      { path: 'experts', element: <Experts />, handle: { title: 'Our Experts' } },
      { path: 'experts/:slug', element: <ExpertDetails />, handle: { title: 'Expert' } },
      { path: 'knowledgecenter', element: <KnowledgeCenter />, handle: { title: 'Knowledge Center' } },
      { path: 'resources', element: <Resources />, handle: { title: 'Resource Library' } },
      { path: 'register', element: <Register />, handle: { title: 'Register' } },
      /* Standalone sign-in / signup — the destination for the nav and for
         marketing links, which cannot open the modal. `/signup` opens on
         sign-in; `/join` (the campaign alias) and `/signup?mode=signup` open
         on create-account. Not to be confused with `/register` above,
         which is the business-network form. */
      // No handle.title: the page titles itself after the form showing.
      { path: 'signup', element: <SignupPage /> },
      { path: 'join', element: <SignupPage /> },
      { path: 'video/:slug', element: <Videos />, handle: { title: 'Video' } },
      // A business's landing page (ads, partner links). /p/ is the short form
      // for ads; both render the same page with the /partners/ canonical. No
      // handle.title: the page titles itself after the business.
      { path: 'partners/:slug', element: <PartnerPage /> },
      { path: 'p/:slug', element: <PartnerPage /> },
      // Linked from the footer and both signup forms' consent checkbox.
      { path: 'privacy', element: <PrivacyPolicy />, handle: { title: 'Privacy Policy' } },
      { path: 'terms', element: <TermsOfUse />, handle: { title: 'Terms of Use' } },
      { path: 'profile', element: <ProtectedRoute><Profile /></ProtectedRoute>, handle: { title: 'My Profile' } },
      { path: 'blob-test', element: <AdminRoute><BlobStorageTest /></AdminRoute> },
      // Unknown URLs land here instead of react-router's bare error screen.
      { path: '*', element: <NotFound />, handle: { title: 'Page not found' } },
    ]
  }
    ]
  }
]);

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const app = (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );

  // Only mount the provider when a client ID exists: it injects Google's
  // Identity Services script, which is useless (and a needless third-party
  // request) without one. SocialAuthButtons hides the Google button under the
  // same condition, so nothing inside ever needs the missing context.
  return GOOGLE_CLIENT_ID ? (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{app}</GoogleOAuthProvider>
  ) : (
    app
  );
}

export default App;
